<template>
  <div>
    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        Screen Life Cycle
        <div class="btn-group">
          <b-button v-b-modal="'add-entry'" size="sm" variant="outline-dark">
            <font-awesome-icon icon="plus"/>
            Add Life Cycle Entry
          </b-button>
        </div>
      </div>
      <div class="card-body">
        <div v-for="(item,index) in machine.items" :key="item.isReport ? item.report.report_longid : item.entry.longid">
          <div v-if="item.isReport">
            <div :id="item.report.report_longid" :class="defineReportEventClass(item.report.user_restricted, index)"
                 :style="defineReportEventStyle(item.report.user_restricted)">
              <div class="card-header d-flex align-items-center justify-content-between">
                <a :href="'#' + item.report.report_longid">
                  <font-awesome-icon icon="list-alt"/>
                  {{ item.report.user_label }} added
                  ScreenCheck Report</a>
                <div class="btn-group float-right ml-2">
                  <b-button v-if="isUserAdmin || isUserEngineeringManager" v-b-modal="'move-report' " size="sm" variant="outline-dark"
                            @click="selectedReport = item.report">
                    <font-awesome-icon icon="exchange-alt"/>
                    Move
                  </b-button>
                </div>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-6">
                    <div v-if="item.report.report_label" title="Title">
                      <font-awesome-icon class="mr-1" icon="list-alt"/>
                      <span class="report_label">{{ item.report.report_label }}</span>
                    </div>
                    <div title="Timestamp">
                      <font-awesome-icon icon="calendar-alt"/>
                      {{ item.report.report_creation }}
                    </div>
                    <div title="Uploader" v-if="item.report.user_restricted" class="text-warning">
                      <font-awesome-icon icon="user-lock" title="This report has been uploaded by a restricted user"/>
                      {{ item.report.user_label }}
                    </div>
                    <div title="Uploader" v-else>
                      <font-awesome-icon icon="user"/>
                      {{ item.report.user_label }}
                    </div>
                    <div title="Technician">
                      <font-awesome-icon icon="wrench"/>
                      {{ item.report.report_author_label }}
                    </div>
                    <div v-if="item.report.report_flavor">
                      <div title="Flavor">
                        <font-awesome-icon icon="glass-martini"/>
                        {{ item.report.report_flavor }}
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div v-if="item.report.report_machine_type">
                      <div title="Screen Type">
                        <font-awesome-icon icon="tag"/>
                        {{ item.report.report_machine_type }}
                      </div>
                    </div>
                    <div v-if="item.report.report_machine_serial">
                      <div title="Screen Serial Number">
                        <font-awesome-icon icon="barcode"/>
                        {{ item.report.report_machine_serial }}
                      </div>
                    </div>
                    <div v-if="item.report.report_machine_owner_label">
                      <div title="Screen Owner">
                        <font-awesome-icon icon="briefcase"/>
                        {{ item.report.report_machine_owner_label }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mt-2">
                  <div v-if="item.report.commenter_user_label">
                    <p class="mb-0" style="white-space:pre-wrap">
                      <font-awesome-icon icon="comment-alt" class="mr-1"/>
                      {{ item.report.report_comment }}
                    </p>
                    <p class="text-muted mb-0">Last edit by {{ item.report.commenter_user_label }}
                      on {{ item.report.report_comment_timestamp }}</p>
                    <b-button-group v-if="canDeleteReportNotes(item.report.commenter_user_label)" class="mt-2">
                      <b-button v-b-modal="'note-report'" size="sm" variant="outline-dark"
                                @click="openNoteModal(item.report)">
                        <font-awesome-icon icon="comment-alt"/>
                        Edit
                      </b-button>
                      <b-button size="sm" variant="outline-dark" @click="openDeleteReportNoteModal(item.report)">
                        <font-awesome-icon icon="trash-can"/>
                        Delete
                      </b-button>
                    </b-button-group>
                  </div>
                  <div v-else>
                    <b-button v-b-modal="'note-report'" size="sm" variant="outline-dark"
                              @click="openNoteModal(item.report)">
                      <font-awesome-icon icon="comment-alt"/>
                      Add Note
                    </b-button>
                  </div>
                </div>
              </div>
              <div class="card-footer">
                <div class="row">
                  <div class="col-sm">
                    <router-link :to="'/report/' + item.report.report_longid">
                      <font-awesome-icon icon="weight"/>
                      Report page
                    </router-link>
                  </div>
                  <div class="col-sm">
                    <div v-if="item.report.report_attachments_xls_file">
                      <a :href="'/frontend-api/report/' + item.report.report_longid + '/_/' + item.report.report_attachments_xls_file + '/' + item.report.report_attachments_xls_filename">
                        <font-awesome-icon icon="download"/>
                        XLS Report
                      </a>
                    </div>
                  </div>
                  <div class="col-sm">
                    <a v-for="image in item.report.report_attachments_images"
                       :key="item.report.report_longid + image.image"
                       :data-lightbox="item.report.report_longid"
                       :href="'/frontend-api/report/' + item.report.report_longid + '/_/' + image.image"
                       class="mr-1">
                      <font-awesome-icon icon="image"/>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div :id="item.entry.longid" class="card">
              <div class="card-header">
                <font-awesome-icon v-if="item.entry.isComment" class="mr-1" icon="comment-alt"/>
                <font-awesome-icon v-if="item.entry.isManual" class="mr-1" icon="file"/>
                <font-awesome-icon v-if="item.entry.isAssemblyLog" class="mr-1" icon="list-alt"/>
                <font-awesome-icon v-if="item.entry.isCwr" class="mr-1" icon="weight"/>
                <a :href="'#' + item.entry.longid">{{ item.entry.label }} added
                  <span v-if="item.entry.isComment">comment</span>
                  <span v-if="item.entry.isManual">manual</span>
                  <span v-if="item.entry.isAssemblyLog">assembly log</span>
                  <span v-if="item.entry.isCwr">CWR</span>
                </a>
                <div class="float-right ml-2">
                  <b-button v-if="$store.getters.canDeleteMachineEntries" size="sm" variant="outline-dark" @click="openDeleteEntryModal(item.entry)">
                    <font-awesome-icon icon="trash-can"/>
                    Delete
                  </b-button>
                </div>
              </div>
              <div class="card-body">
                <div style="white-space:pre-wrap">{{ item.entry.text }}</div>
              </div>
              <div class="card-footer">
                <div class="row">
                  <div class="col-6">
                    <div v-if="item.entry.filename">
                      <a :href="'/frontend-api/entry/' + item.entry.longid + '/' + encodeURIComponent(item.entry.filename)">
                        <font-awesome-icon icon="download"/>
                        {{ item.entry.filename }}
                      </a>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="float-right">
                      {{ item.entry.timestamp }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- moveReport Modal -->
    <b-modal id="move-report" ref="move-report" title="Move Report" @ok="moveReport">
      <b-alert :show="!currentModalValid" variant="warning">
        Please select a destination report
      </b-alert>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Report Label</label>
        <div class="col-sm-9">
          <label>{{ selectedReport && selectedReport.report_label ? selectedReport.report_label : 'Unlabeled' }}</label>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Destination Base File Serial</label>
        <div class="col-sm-9">
          <b-form-select v-model="selectedMachineSerial">
            <option :value="null" disabled>-- Please select an option --</option>
            <option v-for="(otherSerial, index) in machine.otherSerials" :key="index" :value="otherSerial.serial">
              {{ otherSerial.serial }}
            </option>
          </b-form-select>
        </div>
      </div>
    </b-modal>
    <!-- noteReport Modal -->
    <b-modal id="note-report" ref="note-report" title="Edit Report Note" @ok="noteReport">
      <b-alert :show="!currentModalValid" variant="warning">
        The note must not be empty
      </b-alert>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Report Label</label>
        <div class="col-sm-9">
          <b-form-input :placeholder="selectedReport ? selectedReport.report_label: ''" disabled></b-form-input>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Note</label>
        <div class="col-sm-9">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon
                icon="comment-alt"/></span></div>
            <b-form-textarea id="noteText" v-model="addReportNote" class="form-control" name="text" rows="5"
                             style="resize:none"></b-form-textarea>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- add-entry Modal -->
    <b-modal id="add-entry" ref="add-entry" title="Add Life Cycle Entry" @cancel="resetSelections"
             @ok="queryAddLifeCycleEntry">
      <b-alert :show="!currentModalValid" variant="warning">
        Please fill out all mandatory fields
      </b-alert>
      <div class="form-group row">
        <span class="col-sm-2 control-label">Entry Type</span>
        <div class="col-sm-10">
          <b-form-group>
            <b-form-radio-group v-model="addEntryType" stacked>
              <b-form-radio value="comment">Comment</b-form-radio>
              <b-form-radio value="manual">Manual</b-form-radio>
              <b-form-radio value="assemblylog">Assembly Log</b-form-radio>
              <b-form-radio value="cwr">CWR</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-2 control-label">Text <small>(optional)</small></label>
        <div class="col-sm-10">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon
                icon="comment-alt"/></span>
            </div>
            <b-form-textarea id="text" v-model="addEntryText" class="form-control" name="text" rows="5"
                             style="resize:none"></b-form-textarea>
          </div>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-2 control-label">File <small id="fileOptional" v-if="addEntryFileValidation === null">(optional)</small></label>
        <div class="col-sm-10">
          <b-form-file
              v-model="addEntrySelectedFile"
              :state="addEntryFileValidation"
              drop-placeholder="Drop file here..."
              placeholder="Choose a file or drop it here..."
              @change="setSelectedFile"
          ></b-form-file>
        </div>
      </div>
    </b-modal>
    <!-- delete entry Modal -->
    <b-modal id="delete-entry" ref="delete-entry" title="Deleting Screen Life Cycle Entry" @ok="deleteMachineEntry(selectedEntry.longid, $event)"
             ok-title="Delete" ok-variant="danger" @hide="deleteEntryError = null">
      <b-alert :show="deleteEntryError !== null" variant="danger">
        {{ deleteEntryError }}
      </b-alert>
      <p v-if="selectedEntry?.isComment">Category: Comment</p>
      <p v-if="selectedEntry?.isManual">Category: Manual</p>
      <p v-if="selectedEntry?.isAssemblyLog">Category: Assembly Log</p>
      <p v-if="selectedEntry?.isCwr">Category: CWR</p>
      <p v-if="selectedEntry?.text">{{ selectedEntry.text }}</p>
      <p v-if="selectedEntry?.filename">
        <font-awesome-icon icon="file" class="mr-2"/>
        {{ selectedEntry.filename }}
      </p>
    </b-modal>
    <!-- deleteReportNote Modal -->
    <b-modal id="delete-report-note" ref="delete-report-note" title="Deleting Report Note" ok-title="Delete" ok-variant="danger"
             @ok="deleteReportNote(noteToDelete.report_longid, $event)" @hide="deleteReportNoteError = null">
      <b-alert :show="deleteReportNoteError !== null" variant="danger">
        {{ deleteReportNoteError }}
      </b-alert>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Report Label</label>
        <div class="col-sm-9">
          <b-form-input :placeholder="noteToDelete.report_label" disabled></b-form-input>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-3 control-label">Note</label>
        <div class="col-sm-9">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon
                icon="comment-alt"/></span></div>
            <b-form-textarea id="noteText" v-model="noteToDelete.note" class="form-control" name="text" rows="5" :disabled="true"
                             style="resize:none"></b-form-textarea>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>

import { CreateMachineEntryQuery, DetailedMachineInformation } from '@/screencheck_portal_api/src'
import { machinesApi, reportsApi } from '@/backend'
import lightbox from 'lightbox2'
import 'lightbox2/src/css/lightbox.css'

export default {
  name: 'MachineLifeCycle',
  props: {
    machine: {
      type: DetailedMachineInformation
    }
  },
  components: {},
  data () {
    return {
      currentModalValid: true,
      selectedMachineSerial: null,
      selectedReport: null,
      addEntrySelectedFile: null,
      selectedFileContent: null,
      addEntryType: 'comment',
      addEntryText: null,
      addReportNote: null,
      selectedEntry: null,
      deleteEntryError: null,
      deleteReportNoteError: null,
      noteToDelete: {
        note: null,
        report_label: null,
        report_longid: null
      }
    }
  },
  computed: {
    isUserAdmin: function () {
      return this.$store.getters.isUserAdmin
    },
    isUserViewAll: function () {
      return this.$store.getters.isUserViewAll
    },
    isUserEngineeringManager: function () {
      return this.$store.getters.isUserEngineeringManager
    },
    addEntryFileValidation: function () {
      if (this.addEntryType !== 'comment' && !this.addEntrySelectedFile) {
        return false
      }
      return null
    },

  },
  mounted () {
    lightbox.option({
      'fadeDuration': 0,
      'imageFadeDuration': 0,
      'resizeDuration': 0
    })
  },
  methods: {
    deleteReportNote: function (reportLongid, ev) {
      ev.preventDefault()
      reportsApi.deleteReportNote(reportLongid, (error) => {
        if (error) {
          console.error(error.response.body?.message || "Error deleting report note")
          this.deleteReportNoteError = error.response.body?.message || "Error deleting report note"
          return
        }
        this.$refs['delete-report-note'].hide()
        this.deleteReportNoteError = null
        this.$emit('machineUpdated')
      })
    },
    openDeleteReportNoteModal: function (report) {
      this.noteToDelete.note = report.report_comment
      this.noteToDelete.report_label = report.report_label
      this.noteToDelete.report_longid = report.report_longid
      this.$refs['delete-report-note'].show()
    },
    canDeleteReportNotes: function (commenter_user_label) {
      return (commenter_user_label === this.$store.getters.userLabel) || this.isUserViewAll
    },
    openDeleteEntryModal: function (entry) {
      this.selectedEntry = entry
      this.$refs['delete-entry'].show()
    },
    deleteMachineEntry: function (entryLongid, ev) {
      ev.preventDefault()
      machinesApi.deleteMachineEntry(this.machine.basefile.serial, entryLongid, (error) => {
        if (error) {
          console.error('Failed deleting machine entry. ' + error)
          this.deleteEntryError = 'Failed deleting machine entry. ' + error
          return
        }
        this.$refs['delete-entry'].hide()
        this.deleteEntryError = null
        this.$emit('machineUpdated')
      })
    },
    defineReportEventClass: function (user_restricted, index) {
      let classes = 'card'
      if (user_restricted) {
        classes += ' border-warning'
      }
      if (index === this.machine.items.length - 1) {
        classes += ' card-without-bottom-margin'
      }
      return classes
    },
    defineReportEventStyle: function (user_restricted) {
      if (user_restricted) {
        return 'border-width: 3px'
      } else {
        return ''
      }
    },
    openNoteModal: function (report) {
      this.selectedReport = report
      this.addReportNote = report.report_comment
    },
    setSelectedFile (ev) {
      this.addEntrySelectedFile = ev.target.files[0]
    },
    resetSelections: function () {
      this.selectedMachineSerial = null
      this.selectedReport = null
      this.addEntrySelectedFile = null
      this.addEntryType = 'comment'
      this.addEntryText = null
      this.addReportNote = null
      this.currentModalValid = true
    },
    moveReport: function (ev) {
      ev.preventDefault()
      if (!this.selectedReport) {
        console.error('No report selected')
        return
      }
      if (!this.selectedMachineSerial) {
        this.currentModalValid = false
        return
      }
      reportsApi.moveReport(this.selectedReport.report_longid, { machineSerial: this.selectedMachineSerial }, (error => {
        this.resetSelections()
        if (error) {
          console.error('Failed moving report: ' + error)
          return
        }
        this.$emit('machineUpdated')
        this.$refs['move-report'].hide()
      }))
    },
    noteReport: function (ev) {
      ev.preventDefault()
      if (!this.selectedReport) {
        console.error('No report selected')
        return
      }
      if (!this.addReportNote) {
        this.currentModalValid = false
        return
      }
      reportsApi.commentReport(this.selectedReport.report_longid, { comment: this.addReportNote }, (error) => {
        this.resetSelections()
        if (error) {
          console.error('Failed commenting report: ' + error)
          return
        }
        this.$emit('machineUpdated')
        this.$refs['note-report'].hide()
      })
    },
    queryAddLifeCycleEntry: function () {
      if (this.addEntrySelectedFile) {
        const reader = new FileReader()
        reader.readAsDataURL(this.addEntrySelectedFile)
        reader.onload = async () => {
          const base64String = reader.result.split(',')[1] // Remove the data URI prefix

          const createQuery = CreateMachineEntryQuery.constructFromObject({
            category: this.addEntryType,
            text: this.addEntryText,
            filename: this.addEntrySelectedFile.name,
            data: base64String
          })
          this.addLifeCycleEntry(createQuery)
        }
      } else {
        const createQuery = CreateMachineEntryQuery.constructFromObject({
          category: this.addEntryType,
          text: this.addEntryText,
        })
        this.addLifeCycleEntry(createQuery)
      }
    },
    addLifeCycleEntry: function (createQuery) {
      machinesApi.createMachineEntry(this.machine.basefile.serial, createQuery, (error) => {
        this.resetSelections()
        if (error) {
          console.error('Failed creating machine entry: ' + error)
          return
        }
        this.$emit('machineUpdated')
        this.$refs['add-entry'].hide()
      })
    },
  }
}
</script>

<style lang="scss">
div.card:target {
  box-shadow: 0 0 20px yellow;
}

.card-without-bottom-margin {
  margin-bottom: 0 !important;
}
</style>
